import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ExternalLink, Loader2, Palette, Pen, PenLine, Trash2, Type, Upload } from "lucide-react";
import { studentService } from "../services/studentService";
import { certificateService } from "../services/certificateService";
import CreateSignatureModal from "./modals/CreateSignatureModal";

// Signatures want transparency to sit cleanly on the certificate, so no JPEG.
const SIGNATURE_EXT_CONTENT_TYPE = {
  png: "image/png",
  webp: "image/webp",
  svg: "image/svg+xml",
};
const MAX_SIGNATURE_BYTES = 2 * 1024 * 1024; // 2 MB — a signature never needs more.
// Template defaults (mirror DEFAULT_PRIMARY/SECONDARY_COLOR on the backend) —
// shown in the color pickers when the school hasn't set brand colors yet.
const DEFAULT_PRIMARY_COLOR = "#1E3A8A";
const DEFAULT_SECONDARY_COLOR = "#C9A961";

// Curated certificate font styles — mirrors CERT_FONT_STYLES on the backend
// (Hypebook-API/src/api/certificates/templates/cert-styles.ts). We expose
// only these vetted pairings, never a raw font list. "classic" is the default.
const CERT_STYLE_OPTIONS = [
  { id: "classic", label: "Classic", description: "Timeless serif — formal and traditional." },
  { id: "modern", label: "Modern", description: "Clean geometric sans — contemporary and crisp." },
  { id: "elegant", label: "Elegant", description: "High-contrast display serif — refined and distinctive." },
  { id: "academic", label: "Academic", description: "Scholarly serif — sturdy and authoritative." },
];
const DEFAULT_CERT_STYLE = "classic";

// The 3 certificate templates (6 cert types collapse to these). Schools can
// override the house style per group; "" means "use the house style". `type`
// is the representative CertificateType used when previewing the group.
const CERT_TYPE_GROUPS = [
  { template: "graduation", type: "GRADUATION", label: "Graduation" },
  { template: "honour-roll", type: "HONOUR_ROLL", label: "Honour roll" },
  { template: "award", type: "AWARD", label: "Award & other" },
];

// Placeholder sample text per template (mirrors the server's per-type
// samples) — shown when the school hasn't set a custom default.
const CERT_TEXT_SAMPLES = {
  graduation: {
    title: "Certificate of Graduation",
    body: "In recognition of the successful completion of all academic requirements and demonstrated commitment to excellence.",
  },
  "honour-roll": {
    title: "Honour Roll",
    body: "In recognition of outstanding academic achievement and placement on the honour roll.",
  },
  award: {
    title: "Certificate of Achievement",
    body: "In recognition of outstanding achievement and dedication.",
  },
};
const EMPTY_CERT_TEXT = { graduation: { title: "", body: "" }, "honour-roll": { title: "", body: "" }, award: { title: "", body: "" } };

/**
 * Certificate branding panel — signature, signatory name, brand colors,
 * certificate style/fonts, default wording, and a live preview. Applied as
 * defaults to every certificate the school issues. Rendered under the
 * Certificates page's "Branding" tab (previously lived on the Settings page).
 *
 * Reads the school logo (read-only here) purely so the live preview matches
 * what an issued certificate will look like — the logo itself is managed on
 * the Settings page.
 */
export default function CertificateBranding() {
  const { t } = useTranslation();
  const signatureInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [signatureUrl, setSignatureUrl] = useState(null);
  const [savingSignature, setSavingSignature] = useState(false);
  const [showCreateSignature, setShowCreateSignature] = useState(false);
  const [signatoryName, setSignatoryName] = useState("");
  const [signatoryInput, setSignatoryInput] = useState("");
  const [savingSignatory, setSavingSignatory] = useState(false);
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY_COLOR);
  const [secondaryColor, setSecondaryColor] = useState(DEFAULT_SECONDARY_COLOR);
  const [savedColors, setSavedColors] = useState({ primary: "", secondary: "" });
  const [savingColors, setSavingColors] = useState(false);
  const [fontTheme, setFontTheme] = useState(DEFAULT_CERT_STYLE);
  const [savedFontTheme, setSavedFontTheme] = useState(DEFAULT_CERT_STYLE);
  // Per-template overrides, keyed by template ("" = inherit house style).
  const [fontOverrides, setFontOverrides] = useState({ graduation: "", "honour-roll": "", award: "" });
  const [savedFontOverrides, setSavedFontOverrides] = useState({ graduation: "", "honour-roll": "", award: "" });
  const [savingFont, setSavingFont] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewType, setPreviewType] = useState("AWARD");
  // Per-template default cert text (pre-fills issuance). Editable in the
  // preview; "" means "use the built-in sample for that type".
  const [certText, setCertText] = useState(EMPTY_CERT_TEXT);
  const [savedCertText, setSavedCertText] = useState(EMPTY_CERT_TEXT);
  const [savingCertText, setSavingCertText] = useState(false);
  // The school logo — read-only here, used so the live preview matches an
  // actual issued cert. Managed on the Settings page.
  const [logoUrl, setLogoUrl] = useState(null);
  // Live HTML preview (no PDF/Chromium) — re-rendered as branding changes.
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewScale, setPreviewScale] = useState(0.5);
  const previewBoxRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const settings = await studentService.getSchoolSettings();
        if (cancelled) return;
        setLogoUrl(settings.logoUrl || null);
        setSignatureUrl(settings.signatureUrl || null);
        setSignatoryName(settings.signatoryName || "");
        setSignatoryInput(settings.signatoryName || "");
        setPrimaryColor(settings.brandPrimaryColor || DEFAULT_PRIMARY_COLOR);
        setSecondaryColor(settings.brandSecondaryColor || DEFAULT_SECONDARY_COLOR);
        setSavedColors({ primary: settings.brandPrimaryColor || "", secondary: settings.brandSecondaryColor || "" });
        setFontTheme(settings.brandFontTheme || DEFAULT_CERT_STYLE);
        setSavedFontTheme(settings.brandFontTheme || DEFAULT_CERT_STYLE);
        const ov = settings.brandFontThemes || {};
        const overrides = { graduation: ov.graduation || "", "honour-roll": ov["honour-roll"] || "", award: ov.award || "" };
        setFontOverrides(overrides);
        setSavedFontOverrides(overrides);
        const ct = settings.brandCertText || {};
        const loadedText = {
          graduation: { title: ct.graduation?.title || "", body: ct.graduation?.body || "" },
          "honour-roll": { title: ct["honour-roll"]?.title || "", body: ct["honour-roll"]?.body || "" },
          award: { title: ct.award?.title || "", body: ct.award?.body || "" },
        };
        setCertText(loadedText);
        setSavedCertText(loadedText);
      } catch (error) {
        if (!cancelled) toast.error(error.message || t("settings.loadFailed", { defaultValue: "Failed to load settings" }));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const handleSignatureFile = async (file) => {
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    const contentType = SIGNATURE_EXT_CONTENT_TYPE[ext];
    if (!contentType) {
      toast.error(t("settings.signatureBadType", { defaultValue: "Use a transparent PNG, WEBP, or SVG image." }));
      return;
    }
    if (file.size > MAX_SIGNATURE_BYTES) {
      toast.error(t("settings.signatureTooBig", { defaultValue: "Signature must be under 2 MB." }));
      return;
    }
    setSavingSignature(true);
    try {
      const updated = await studentService.uploadSchoolSignature(file, contentType);
      setSignatureUrl(updated.signatureUrl || null);
      toast.success(t("settings.signatureSaved", { defaultValue: "Signature updated" }));
    } catch (error) {
      toast.error(error.message || t("settings.signatureFailed", { defaultValue: "Couldn't update the signature" }));
    } finally {
      setSavingSignature(false);
      if (signatureInputRef.current) signatureInputRef.current.value = "";
    }
  };

  const handleRemoveSignature = async () => {
    if (!window.confirm(t("settings.signatureRemoveConfirm", { defaultValue: "Remove the signature image?" }))) return;
    setSavingSignature(true);
    try {
      await studentService.updateSchoolSettings({ signatureUrl: null });
      setSignatureUrl(null);
      toast.success(t("settings.signatureRemoved", { defaultValue: "Signature removed" }));
    } catch (error) {
      toast.error(error.message || t("settings.signatureFailed", { defaultValue: "Couldn't update the signature" }));
    } finally {
      setSavingSignature(false);
    }
  };

  const handleSaveSignatory = async () => {
    const trimmed = signatoryInput.trim();
    if (trimmed === signatoryName) return;
    setSavingSignatory(true);
    try {
      const updated = await studentService.updateSchoolSettings({ signatoryName: trimmed });
      setSignatoryName(updated.signatoryName || "");
      setSignatoryInput(updated.signatoryName || "");
      toast.success(t("settings.signatorySaved", { defaultValue: "Signatory name updated" }));
    } catch (error) {
      toast.error(error.message || t("settings.signatoryFailed", { defaultValue: "Couldn't update the signatory name" }));
    } finally {
      setSavingSignatory(false);
    }
  };

  const colorsDirty = primaryColor !== (savedColors.primary || DEFAULT_PRIMARY_COLOR) || secondaryColor !== (savedColors.secondary || DEFAULT_SECONDARY_COLOR);

  const handleSaveColors = async () => {
    if (!colorsDirty) return;
    setSavingColors(true);
    try {
      const updated = await studentService.updateSchoolSettings({
        brandPrimaryColor: primaryColor,
        brandSecondaryColor: secondaryColor,
      });
      const p = updated.brandPrimaryColor || "";
      const s = updated.brandSecondaryColor || "";
      setSavedColors({ primary: p, secondary: s });
      setPrimaryColor(p || DEFAULT_PRIMARY_COLOR);
      setSecondaryColor(s || DEFAULT_SECONDARY_COLOR);
      toast.success(t("settings.colorsSaved", { defaultValue: "Brand colors updated" }));
    } catch (error) {
      toast.error(error.message || t("settings.colorsFailed", { defaultValue: "Couldn't update brand colors" }));
    } finally {
      setSavingColors(false);
    }
  };

  const overridesDirty = CERT_TYPE_GROUPS.some((g) => fontOverrides[g.template] !== savedFontOverrides[g.template]);
  const fontDirty = fontTheme !== savedFontTheme || overridesDirty;

  const handleSaveFont = async () => {
    if (!fontDirty) return;
    setSavingFont(true);
    try {
      // Only send non-empty overrides; "" means "inherit the house style".
      const brandFontThemes = {};
      for (const g of CERT_TYPE_GROUPS) {
        if (fontOverrides[g.template]) brandFontThemes[g.template] = fontOverrides[g.template];
      }
      await studentService.updateSchoolSettings({ brandFontTheme: fontTheme, brandFontThemes });
      // A successful save persisted exactly what we sent — keep the user's
      // selection on screen and just sync the "saved" baseline so the form is
      // no longer dirty. We deliberately don't re-read the response: its shape
      // can lag the UI during a deploy, and echoing a missing field back would
      // wrongly snap the dropdowns to the default.
      setSavedFontTheme(fontTheme);
      setSavedFontOverrides({
        graduation: fontOverrides.graduation,
        "honour-roll": fontOverrides["honour-roll"],
        award: fontOverrides.award,
      });
      toast.success(t("settings.fontSaved", { defaultValue: "Certificate style updated" }));
    } catch (error) {
      toast.error(error.message || t("settings.fontFailed", { defaultValue: "Couldn't update the certificate style" }));
    } finally {
      setSavingFont(false);
    }
  };

  // Effective style for a cert type = its per-template override, else house.
  const effectiveStyleForType = (certType) => {
    const group = CERT_TYPE_GROUPS.find((g) => g.type === certType);
    return (group && fontOverrides[group.template]) || fontTheme;
  };

  // The template group currently shown in the preview + its editable text.
  const previewTemplate = CERT_TYPE_GROUPS.find((g) => g.type === previewType)?.template || "award";
  const currentText = certText[previewTemplate] || { title: "", body: "" };
  const setCurrentText = (field, value) =>
    setCertText((prev) => ({ ...prev, [previewTemplate]: { ...prev[previewTemplate], [field]: value } }));

  const certTextDirty = CERT_TYPE_GROUPS.some(
    (g) => certText[g.template].title !== savedCertText[g.template].title || certText[g.template].body !== savedCertText[g.template].body,
  );

  const handleSaveCertText = async () => {
    if (!certTextDirty) return;
    setSavingCertText(true);
    try {
      const brandCertText = {};
      for (const g of CERT_TYPE_GROUPS) {
        const title = certText[g.template].title.trim();
        const body = certText[g.template].body.trim();
        if (title || body) brandCertText[g.template] = { title, body };
      }
      await studentService.updateSchoolSettings({ brandCertText });
      // Trust the local state on a successful save (see handleSaveFont).
      setSavedCertText({
        graduation: { ...certText.graduation },
        "honour-roll": { ...certText["honour-roll"] },
        award: { ...certText.award },
      });
      toast.success(t("settings.certTextSaved", { defaultValue: "Default text saved" }));
    } catch (error) {
      toast.error(error.message || t("settings.certTextFailed", { defaultValue: "Couldn't save the default text" }));
    } finally {
      setSavingCertText(false);
    }
  };

  // The live-preview payload — current on-screen branding for the selected
  // type. Memoized so the render effect only fires on real changes.
  const previewPayload = useMemo(() => {
    const group = CERT_TYPE_GROUPS.find((g) => g.type === previewType);
    const tmpl = group?.template;
    const effective = (tmpl && fontOverrides[tmpl]) || fontTheme;
    const text = (tmpl && certText[tmpl]) || { title: "", body: "" };
    return {
      type: previewType,
      primaryColor,
      secondaryColor,
      signatoryName: signatoryInput.trim() || undefined,
      signatureUrl: signatureUrl || undefined,
      logoUrl: logoUrl || undefined,
      fontTheme: effective,
      // Custom default text wins; empty → server uses the per-type sample.
      title: text.title.trim() || undefined,
      bodyText: text.body.trim() || undefined,
    };
  }, [previewType, primaryColor, secondaryColor, signatoryInput, signatureUrl, logoUrl, fontTheme, fontOverrides, certText]);

  // Scale the A4-landscape cert (1123×794px) to fit the preview box width.
  useEffect(() => {
    const el = previewBoxRef.current;
    if (!el) return;
    const update = () => setPreviewScale(el.clientWidth / 1123);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading]);

  // Debounced live render via the cheap HTML endpoint (no Chromium).
  useEffect(() => {
    if (loading) return;
    let cancelled = false;
    setPreviewLoading(true);
    const timer = setTimeout(async () => {
      try {
        const html = await certificateService.previewCertificateHtml(previewPayload);
        if (!cancelled) setPreviewHtml(html);
      } catch {
        // Keep the last good preview on a transient error.
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [previewPayload, loading]);

  // Render a watermarked sample cert with the CURRENT on-screen branding
  // (including unsaved colors / signatory / style) and open it in a new tab.
  const handlePreviewCert = async () => {
    setPreviewing(true);
    try {
      const blob = await certificateService.previewCertificate({
        type: previewType,
        primaryColor,
        secondaryColor,
        signatoryName: signatoryInput.trim() || undefined,
        signatureUrl: signatureUrl || undefined,
        logoUrl: logoUrl || undefined,
        fontTheme: effectiveStyleForType(previewType),
      });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) {
        // Popup blocked — fall back to a download.
        const a = document.createElement("a");
        a.href = url;
        a.download = "certificate-preview.pdf";
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      toast.error(error.message || t("settings.previewFailed", { defaultValue: "Couldn't generate the preview" }));
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-blue-100 p-12 flex justify-center items-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <>
      {showCreateSignature && (
        <CreateSignatureModal
          onClose={() => setShowCreateSignature(false)}
          onSaved={(updated) => {
            setSignatureUrl(updated.signatureUrl || null);
            setShowCreateSignature(false);
            toast.success(t("settings.signatureSaved", { defaultValue: "Signature saved" }));
          }}
        />
      )}

      <div className="bg-white rounded-2xl border border-blue-100 p-6 flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <h3 className="text-gray-900 text-lg font-bold">{t("settings.brandingTitle", { defaultValue: "Certificate branding" })}</h3>
          <p className="text-sm text-gray-500">
            {t("settings.brandingHelp", {
              defaultValue: "Applied to the certificates you issue — a signature, the signatory's name, and your brand colors.",
            })}
          </p>
        </div>

        {/* Controls (left) and the live preview (right, sticky) sit side by
            side on large screens so branding changes are visible without
            scrolling. Stacks to one column below lg. */}
        <div className="grid lg:grid-cols-2 gap-x-8 gap-y-6 items-start">
          <div className="flex flex-col gap-6 min-w-0">
            {/* Signature image */}
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
                <PenLine size={15} className="text-primary" />
                {t("settings.signatureTitle", { defaultValue: "Signature image" })}
              </div>
              <div className="flex items-center gap-5">
                <div className="w-40 h-16 rounded-xl border border-gray-200 bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
                  {signatureUrl ? (
                    <img src={signatureUrl} alt={t("settings.signatureAlt", { defaultValue: "Signature" })} className="w-full h-full object-contain p-1" />
                  ) : (
                    <PenLine className="w-7 h-7 text-gray-300" />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <input
                    ref={signatureInputRef}
                    type="file"
                    accept=".png,.webp,.svg,image/png,image/webp,image/svg+xml"
                    className="hidden"
                    onChange={(e) => handleSignatureFile(e.target.files?.[0])}
                  />
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => signatureInputRef.current?.click()}
                      disabled={savingSignature}
                      className="h-10 px-4 bg-primary hover:bg-blue-600 text-white text-sm font-semibold rounded-lg flex items-center gap-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {savingSignature ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                      {signatureUrl
                        ? t("settings.replaceSignature", { defaultValue: "Replace" })
                        : t("settings.uploadSignature", { defaultValue: "Upload signature" })}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCreateSignature(true)}
                      disabled={savingSignature}
                      className="h-10 px-4 border border-blue-200 text-primary text-sm font-semibold rounded-lg flex items-center gap-2 hover:bg-blue-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <Pen size={16} />
                      {t("settings.createSignature", { defaultValue: "Draw or type" })}
                    </button>
                    {signatureUrl && (
                      <button
                        type="button"
                        onClick={handleRemoveSignature}
                        disabled={savingSignature}
                        className="h-10 px-4 border border-gray-200 text-gray-700 text-sm font-semibold rounded-lg flex items-center gap-2 hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <Trash2 size={16} />
                        {t("settings.removeSignature", { defaultValue: "Remove" })}
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-400">
                    {t("settings.signatureTip", { defaultValue: "A transparent PNG of a handwritten signature looks best." })}
                  </p>
                </div>
              </div>
            </div>

            {/* Signatory name */}
            <div className="border-t border-gray-100 pt-4 flex flex-col gap-2">
              <label className="text-sm font-semibold text-gray-900">{t("settings.signatoryTitle", { defaultValue: "Signatory name" })}</label>
              <p className="text-xs text-gray-500">
                {t("settings.signatoryHelp", { defaultValue: 'Shown beneath the signature line — e.g. "Dr. Sam Rivera, Principal".' })}
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={signatoryInput}
                  onChange={(e) => setSignatoryInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveSignatory();
                  }}
                  maxLength={120}
                  placeholder={t("settings.signatoryPlaceholder", { defaultValue: "Dr. Sam Rivera, Principal" })}
                  className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:border-primary"
                />
                <button
                  type="button"
                  onClick={handleSaveSignatory}
                  disabled={savingSignatory || signatoryInput.trim() === signatoryName}
                  className="h-10 px-4 bg-primary hover:bg-blue-600 text-white text-sm font-semibold rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingSignatory ? <Loader2 size={16} className="animate-spin" /> : null}
                  {t("settings.saveName", { defaultValue: "Save" })}
                </button>
              </div>
            </div>

            {/* Brand colors */}
            <div className="border-t border-gray-100 pt-4 flex flex-col gap-3">
              <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
                <Palette size={15} className="text-primary" />
                {t("settings.colorsTitle", { defaultValue: "Brand colors" })}
              </div>
              <p className="text-xs text-gray-500">
                {t("settings.colorsHelp", { defaultValue: "Used as defaults on issued certificates. Falls back to navy & gold if unset." })}
              </p>
              <div className="flex items-center gap-5 flex-wrap">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="color"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="h-9 w-12 rounded border border-gray-200 bg-white cursor-pointer"
                  />
                  <span className="flex flex-col">
                    <span className="font-medium">{t("settings.colorPrimary", { defaultValue: "Primary" })}</span>
                    <span className="text-xs text-gray-400 uppercase">{primaryColor}</span>
                  </span>
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="color"
                    value={secondaryColor}
                    onChange={(e) => setSecondaryColor(e.target.value)}
                    className="h-9 w-12 rounded border border-gray-200 bg-white cursor-pointer"
                  />
                  <span className="flex flex-col">
                    <span className="font-medium">{t("settings.colorSecondary", { defaultValue: "Secondary" })}</span>
                    <span className="text-xs text-gray-400 uppercase">{secondaryColor}</span>
                  </span>
                </label>
                <button
                  type="button"
                  onClick={handleSaveColors}
                  disabled={savingColors || !colorsDirty}
                  className="h-10 px-4 bg-primary hover:bg-blue-600 text-white text-sm font-semibold rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingColors ? <Loader2 size={16} className="animate-spin" /> : null}
                  {t("settings.saveColors", { defaultValue: "Save colors" })}
                </button>
              </div>
            </div>

            {/* Certificate style (fonts) */}
            <div className="border-t border-gray-100 pt-4 flex flex-col gap-3">
              <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
                <Type size={15} className="text-primary" />
                {t("settings.fontTitle", { defaultValue: "Certificate style" })}
              </div>
              <p className="text-xs text-gray-500">
                {t("settings.fontHelp", { defaultValue: "Sets the fonts on the certificates you issue. Applied to new certificates; existing ones keep their original style." })}
              </p>
              <label className="flex flex-col gap-1 text-sm text-gray-700 max-w-md">
                <span className="font-medium">{t("settings.fontDefault", { defaultValue: "Default style" })}</span>
                <select
                  value={fontTheme}
                  onChange={(e) => setFontTheme(e.target.value)}
                  className="h-10 px-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-200"
                >
                  {CERT_STYLE_OPTIONS.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </select>
                <span className="text-xs text-gray-400">{CERT_STYLE_OPTIONS.find((s) => s.id === fontTheme)?.description}</span>
              </label>

              {/* Optional per-type overrides — "" inherits the default style. */}
              <div className="flex flex-col gap-2 pt-1">
                <span className="text-xs font-semibold text-gray-600">
                  {t("settings.fontPerType", { defaultValue: "Different style for specific types (optional)" })}
                </span>
                {CERT_TYPE_GROUPS.map((g) => (
                  <div key={g.template} className="flex items-center gap-3">
                    <span className="text-sm text-gray-700 w-32 flex-shrink-0">{g.label}</span>
                    <select
                      value={fontOverrides[g.template]}
                      onChange={(e) => setFontOverrides((prev) => ({ ...prev, [g.template]: e.target.value }))}
                      className="h-9 px-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-200 min-w-[200px]"
                    >
                      <option value="">{t("settings.fontUseDefault", { defaultValue: "Use default style" })}</option>
                      {CERT_STYLE_OPTIONS.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              <div>
                <button
                  type="button"
                  onClick={handleSaveFont}
                  disabled={savingFont || !fontDirty}
                  className="h-10 px-4 bg-primary hover:bg-blue-600 text-white text-sm font-semibold rounded-lg inline-flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingFont ? <Loader2 size={16} className="animate-spin" /> : null}
                  {t("settings.saveFont", { defaultValue: "Save style" })}
                </button>
              </div>
            </div>
          </div>
          {/* ── end controls column ── */}

          {/* Live preview — sticky beside the controls on large screens so
              it stays in view as you adjust branding. */}
          <div className="lg:sticky lg:top-6 flex flex-col gap-3 min-w-0">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-900">{t("settings.previewTitle", { defaultValue: "Live preview" })}</span>
                {previewLoading && previewHtml && <Loader2 size={14} className="animate-spin text-gray-400" />}
              </div>
              <div className="flex items-center gap-3">
                <select
                  value={previewType}
                  onChange={(e) => setPreviewType(e.target.value)}
                  className="h-9 px-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-200"
                >
                  {CERT_TYPE_GROUPS.map((g) => (
                    <option key={g.type} value={g.type}>
                      {g.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={handlePreviewCert}
                  disabled={previewing}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline disabled:opacity-60"
                >
                  {previewing ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />}
                  {t("settings.previewOpenPdf", { defaultValue: "Open PDF" })}
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500">
              {t("settings.previewHelp", {
                defaultValue: "Updates as you change signature, signatory, colors, and style — including unsaved changes.",
              })}
            </p>

            {/* Editable default text for the selected type — pre-fills the
                Issue Certificate form once saved. Empty → built-in sample. */}
            <div className="rounded-xl border border-gray-200 p-3 flex flex-col gap-2.5 bg-gray-50/60">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold text-gray-600">
                  {t("settings.certTextTitle", { defaultValue: "Default wording for {{type}}", type: CERT_TYPE_GROUPS.find((g) => g.type === previewType)?.label })}
                </span>
                <button
                  type="button"
                  onClick={handleSaveCertText}
                  disabled={savingCertText || !certTextDirty}
                  className="h-8 px-3 bg-primary hover:bg-blue-600 text-white text-xs font-semibold rounded-lg inline-flex items-center gap-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingCertText ? <Loader2 size={14} className="animate-spin" /> : null}
                  {t("settings.saveCertText", { defaultValue: "Save as default" })}
                </button>
              </div>
              <input
                type="text"
                value={currentText.title}
                onChange={(e) => setCurrentText("title", e.target.value)}
                placeholder={CERT_TEXT_SAMPLES[previewTemplate]?.title}
                maxLength={120}
                className="h-9 px-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
              <textarea
                value={currentText.body}
                onChange={(e) => setCurrentText("body", e.target.value)}
                placeholder={CERT_TEXT_SAMPLES[previewTemplate]?.body}
                maxLength={600}
                rows={2}
                className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 resize-none focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
              <span className="text-[11px] text-gray-400">
                {t("settings.certTextHint", { defaultValue: "Leave blank to use the built-in wording. Saved wording pre-fills the Issue Certificate form." })}
              </span>
            </div>

            <div ref={previewBoxRef} className="w-full rounded-xl border border-gray-200 overflow-hidden bg-gray-100">
              <div className="relative" style={{ height: Math.max(120, Math.round(794 * previewScale)) }}>
                {previewHtml ? (
                  <iframe
                    title={t("settings.previewTitle", { defaultValue: "Live preview" })}
                    srcDoc={previewHtml}
                    sandbox=""
                    className="border-0"
                    style={{ width: 1123, height: 794, transform: `scale(${previewScale})`, transformOrigin: "0 0", pointerEvents: "none" }}
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm">
                    <Loader2 size={18} className="animate-spin mr-2" />
                    {t("settings.previewRendering", { defaultValue: "Rendering preview…" })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
